import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Eye, Envelope, Phone, Briefcase, Sparkle, CircleNotch, X, FilePdf, Funnel, MapPin, Clock, Certificate, CalendarBlank, CaretLeft, CaretRight, MagnifyingGlass, Star, Tag } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const CandidatesList = () => {
  const { token, hasPage } = useAuth();
  const canDelete = hasPage('delete-candidate');
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('quick'); // 'quick' (indexed, default) | 'ai' (LLM-based, opt-in only — expensive)
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [resultsFromAi, setResultsFromAi] = useState(false);
  const isAiSearchActive = isSearchActive && resultsFromAi;
  const [filterRole, setFilterRole] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCertification, setFilterCertification] = useState(searchParams.get('certification') || '');
  const [tagOptions, setTagOptions] = useState([]);
  const [filterTagIds, setFilterTagIds] = useState([]);
  const [filterMinYears, setFilterMinYears] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterFavoritesOnly, setFilterFavoritesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchCandidates();
    axios.get(`${API}/tags/options`)
      .then(({ data }) => setTagOptions(data))
      .catch((error) => console.error('Error fetching tag filters:', error));
  }, []);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // AI search is expensive (LLM call), so it never runs automatically — only quick search auto-triggers.
    if (searchMode !== 'quick') {
      return;
    }

    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setSearchResults([]);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      performQuickSearch(searchQuery);
    }, 400);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, searchMode]);

  const fetchCandidates = async () => {
    try {
      const response = await axios.get(`${API}/candidates`);
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const performQuickSearch = async (query) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setIsSearchActive(true);
    setResultsFromAi(false);
    try {
      const response = await axios.get(`${API}/candidates/quick-search`, { params: { q: query } });
      setSearchResults(response.data.map((c) => ({ candidate: c, relevance_score: null, match_reason: null })));
    } catch (error) {
      console.error('Quick search error:', error);
      toast.error('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const performAiSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;

    setIsSearching(true);
    setIsSearchActive(true);
    setResultsFromAi(true);
    try {
      const response = await axios.post(`${API}/candidates/search`, { query });
      setSearchResults(response.data);
    } catch (error) {
      console.error('AI search error:', error);
      toast.error('AI search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchMode = (mode) => {
    setSearchMode(mode);
    if (mode === 'ai') {
      // Never auto-run AI search on mode switch — user must explicitly trigger it.
      setSearchResults([]);
      setIsSearchActive(false);
      setResultsFromAi(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchActive(false);
    setResultsFromAi(false);
  };

  const deleteCandidate = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this candidate?')) {
      return;
    }

    try {
      await axios.delete(`${API}/candidates/${id}`);
      toast.success('Candidate deleted successfully');
      fetchCandidates();
      if (searchQuery && resultsFromAi) {
        performAiSearch();
      } else if (searchQuery) {
        performQuickSearch(searchQuery);
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Failed to delete candidate');
    }
  };

  const toggleFavorite = async (candidate, e) => {
    e.preventDefault();
    e.stopPropagation();
    const nextValue = !candidate.is_favorite;
    try {
      const { data } = await axios.patch(`${API}/candidates/${candidate.id}/favorite`, { is_favorite: nextValue });
      setCandidates((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      setSearchResults((prev) => prev.map((r) => (r.candidate.id === data.id ? { ...r, candidate: data } : r)));
    } catch (error) {
      console.error('Error updating favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  // Determine what to display: search results (with reasons) or all candidates
  const baseItems = isSearchActive
    ? searchResults
    : candidates.map((c) => ({ candidate: c, relevance_score: null, match_reason: null }));

  // Apply filters (client-side)
  const filterRoleLc = filterRole.trim().toLowerCase();
  const filterUploadedByLc = filterUploadedBy.trim().toLowerCase();
  const filterLocationLc = filterLocation.trim().toLowerCase();
  const filterCertificationLc = filterCertification.trim().toLowerCase();
  const minYears = filterMinYears !== '' ? parseFloat(filterMinYears) : null;

  const displayItems = baseItems.filter(({ candidate }) => {
    const candidateDate = candidate.created_at ? new Date(candidate.created_at).toISOString().slice(0, 10) : '';
    if (filterFavoritesOnly && !candidate.is_favorite) return false;
    if (filterRoleLc && !(candidate.role_applied_for || '').toLowerCase().includes(filterRoleLc)) return false;
    if (filterUploadedByLc && !`${candidate.uploaded_by_name || ''} ${candidate.uploaded_by_email || ''}`.toLowerCase().includes(filterUploadedByLc)) return false;
    if (filterLocationLc && !(candidate.location || '').toLowerCase().includes(filterLocationLc)) return false;
    if (filterCertificationLc && !(candidate.certifications || '').toLowerCase().includes(filterCertificationLc)) return false;
    if (filterTagIds.length && !(candidate.tags || []).some((tag) => filterTagIds.includes(tag.id))) return false;
    if (filterFromDate && (!candidateDate || candidateDate < filterFromDate)) return false;
    if (filterToDate && (!candidateDate || candidateDate > filterToDate)) return false;
    if (minYears !== null && !isNaN(minYears)) {
      const yr = candidate.years_experience;
      if (yr === null || yr === undefined || yr < minYears) return false;
    }
    return true;
  });

  const activeFiltersCount = [filterRole, filterUploadedBy, filterLocation, filterCertification, filterMinYears, filterFromDate, filterToDate].filter(Boolean).length + filterTagIds.length + (filterFavoritesOnly ? 1 : 0);
  const clearFilters = () => {
    setFilterRole('');
    setFilterUploadedBy('');
    setFilterLocation('');
    setFilterCertification('');
    setFilterTagIds([]);
    setFilterMinYears('');
    setFilterFromDate('');
    setFilterToDate('');
    setFilterFavoritesOnly(false);
    setSearchParams((params) => { params.delete('certification'); return params; });
  };

  const totalPages = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedItems = displayItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, isSearchActive, filterRole, filterUploadedBy, filterLocation, filterCertification, filterTagIds, filterMinYears, filterFromDate, filterToDate]);

  // Build unique role & location values for datalist suggestions
  const uniqueRoles = Array.from(new Set(candidates.map(c => c.role_applied_for).filter(Boolean))).sort();
  const uniqueUploaders = Array.from(new Set(candidates.map(c => c.uploaded_by_name).filter(Boolean))).sort();
  const uniqueLocations = Array.from(new Set(candidates.map(c => c.location).filter(Boolean))).sort();

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white border border-slate-200 rounded-lg skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="candidates-list-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Candidates
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Manage and review all candidate profiles
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          {/* Search Mode Toggle */}
          <div className="flex items-center space-x-2 mb-2">
            <button
              type="button"
              onClick={() => selectSearchMode('quick')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                searchMode === 'quick'
                  ? 'bg-[#002FA7] text-white border-[#002FA7]'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="search-mode-quick"
            >
              <MagnifyingGlass size={14} />
              <span>Quick Search</span>
            </button>
            <button
              type="button"
              onClick={() => selectSearchMode('ai')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                searchMode === 'ai'
                  ? 'bg-[#002FA7] text-white border-[#002FA7]'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="search-mode-ai"
              title="Uses AI to understand natural-language queries — slower and costs AI credits"
            >
              <Sparkle size={14} weight="duotone" />
              <span>AI Search</span>
            </button>
          </div>

          <div className="relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {isSearching ? (
                <CircleNotch size={20} className="text-[#002FA7] animate-spin" />
              ) : searchMode === 'ai' ? (
                <Sparkle size={20} weight="duotone" className="text-[#002FA7]" />
              ) : (
                <MagnifyingGlass size={20} className="text-[#002FA7]" />
              )}
            </div>
            <input
              type="text"
              placeholder={
                searchMode === 'ai'
                  ? 'Try: "Python developers from MIT" — press Enter to run AI search'
                  : 'Search by name, role, skills, company, certification...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchMode === 'ai') {
                  performAiSearch();
                }
              }}
              className="w-full pl-12 pr-24 py-3.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="ai-search-input"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {searchMode === 'ai' && searchQuery.trim() && !isSearching && (
                <button
                  onClick={performAiSearch}
                  className="px-2.5 py-1.5 bg-[#002FA7] text-white rounded text-xs font-medium hover:bg-[#00238a] transition-colors"
                  data-testid="run-ai-search-button"
                >
                  Search
                </button>
              )}
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="clear-search-button"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Search Status */}
          {isSearchActive && (
            <div className="mt-3 flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md" data-testid="ai-search-status">
              {resultsFromAi ? (
                <Sparkle size={16} weight="fill" className="text-[#002FA7]" />
              ) : (
                <MagnifyingGlass size={16} weight="bold" className="text-[#002FA7]" />
              )}
              <p className="text-sm text-slate-700">
                {isSearching ? (
                  resultsFromAi ? (
                    <span>AI is analyzing candidates for "<strong>{searchQuery}</strong>"...</span>
                  ) : (
                    <span>Searching for "<strong>{searchQuery}</strong>"...</span>
                  )
                ) : resultsFromAi ? (
                  <span>
                    AI found <strong>{searchResults.length}</strong> candidate{searchResults.length !== 1 ? 's' : ''} matching "<strong>{searchQuery}</strong>"
                  </span>
                ) : (
                  <span>
                    Found <strong>{searchResults.length}</strong> candidate{searchResults.length !== 1 ? 's' : ''} matching "<strong>{searchQuery}</strong>"
                  </span>
                )}
              </p>
            </div>
          )}
          {searchMode === 'ai' && !isSearchActive && searchQuery.trim() && !isSearching && (
            <p className="mt-2 text-xs text-slate-500" data-testid="ai-search-hint">
              Press Enter or click Search to run the AI search (uses AI credits).
            </p>
          )}
        </div>

        {/* Filter Bar */}
        <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4" data-testid="filter-bar">
          <div className="flex items-center space-x-2 mb-3">
            <Funnel size={16} weight="duotone" className="text-[#002FA7]" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
            <button
              type="button"
              onClick={() => setFilterFavoritesOnly((v) => !v)}
              className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                filterFavoritesOnly
                  ? 'bg-amber-50 text-amber-600 border-amber-300'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="filter-favorites-only"
            >
              <Star size={13} weight={filterFavoritesOnly ? 'fill' : 'regular'} />
              <span>Favorites only</span>
            </button>
            {activeFiltersCount > 0 && (
              <>
                <span className="text-xs font-medium bg-blue-50 text-[#002FA7] px-2 py-0.5 rounded" data-testid="filters-active-count">
                  {activeFiltersCount} active
                </span>
                <button
                  onClick={clearFilters}
                  className="text-xs text-slate-500 hover:text-red-600 transition-colors underline underline-offset-2"
                  data-testid="clear-filters-button"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Briefcase size={12} />
                <span>Role Applied For</span>
              </label>
              <input
                type="text"
                list="role-suggestions"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                placeholder="e.g. Senior Engineer"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-role-input"
              />
              <datalist id="role-suggestions">
                {uniqueRoles.map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <span>Uploaded by</span>
              </label>
              <input
                type="text"
                list="uploader-suggestions"
                value={filterUploadedBy}
                onChange={(e) => setFilterUploadedBy(e.target.value)}
                placeholder="e.g. Priya Sharma"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-uploaded-by-input"
              />
              <datalist id="uploader-suggestions">
                {uniqueUploaders.map((uploader) => <option key={uploader} value={uploader} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <MapPin size={12} />
                <span>Location</span>
              </label>
              <input
                type="text"
                list="location-suggestions"
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                placeholder="e.g. Bangalore"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-location-input"
              />
              <datalist id="location-suggestions">
                {uniqueLocations.map((l) => <option key={l} value={l} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Certificate size={12} />
                <span>Certification</span>
              </label>
              <input
                type="text"
                value={filterCertification}
                onChange={(e) => {
                  setFilterCertification(e.target.value);
                  setSearchParams((params) => {
                    if (e.target.value) params.set('certification', e.target.value);
                    else params.delete('certification');
                    return params;
                  });
                }}
                placeholder="e.g. AWS Certified"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-certification-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Tag size={12} />
                <span>Tags</span>
              </label>
              <details className="relative" data-testid="filter-tags-dropdown">
                <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <span>
                    {filterTagIds.length
                      ? `${filterTagIds.length} selected`
                      : 'Select tags'}
                  </span>
                  <CaretRight size={14} className="rotate-90 text-slate-500" />
                </summary>
                <div className="absolute z-20 mt-1 max-h-56 w-full min-w-52 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                  {tagOptions.length ? tagOptions.map((tag) => (
                    <label key={tag.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={filterTagIds.includes(tag.id)}
                        onChange={() => setFilterTagIds((selected) => (
                          selected.includes(tag.id)
                            ? selected.filter((id) => id !== tag.id)
                            : [...selected, tag.id]
                        ))}
                        className="h-4 w-4 accent-[#C82EEB]"
                        data-testid={`filter-tag-${tag.id}`}
                      />
                      <span>{tag.name}</span>
                    </label>
                  )) : (
                    <p className="px-2 py-1.5 text-xs text-slate-500">No tags available</p>
                  )}
                </div>
              </details>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Clock size={12} />
                <span>Min. Years of Experience</span>
              </label>
              <input
                type="number"
                min="0"
                max="60"
                value={filterMinYears}
                onChange={(e) => setFilterMinYears(e.target.value)}
                placeholder="e.g. 5"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-min-years-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <CalendarBlank size={12} />
                <span>Added from</span>
              </label>
              <input
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-from-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <CalendarBlank size={12} />
                <span>Added to</span>
              </label>
              <input
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-to-date-input"
              />
            </div>
          </div>
          {activeFiltersCount > 0 && (
            <div className="mt-3 text-xs text-slate-500" data-testid="filter-result-count">
              Showing <strong className="text-slate-800">{displayItems.length}</strong> of {baseItems.length} candidates
            </div>
          )}
        </div>

        {displayItems.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <p className="text-base text-slate-600">
              {isSearchActive
                ? 'No candidates found matching your query. Try different keywords or a broader search.'
                : 'No candidates yet. Start by uploading CVs.'}
            </p>
            {!isSearchActive && (
              <Link
                to="/upload"
                className="inline-block mt-4 btn-primary px-6 py-2 rounded-md text-sm font-medium"
              >
                Upload CVs
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="candidates-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Rounds
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      {isAiSearchActive ? 'AI Match' : 'Status'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {paginatedItems.map(({ candidate, relevance_score, match_reason }) => (
                    <tr
                      key={candidate.id}
                      className="candidate-row"
                      data-testid={`candidate-row-${candidate.id}`}
                    >
                      <td className="px-6 py-4 min-w-[220px]">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => toggleFavorite(candidate, e)}
                            className={`p-0.5 rounded transition-colors ${candidate.is_favorite ? 'text-amber-400 hover:text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                            title={candidate.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                            data-testid={`favorite-toggle-${candidate.id}`}
                          >
                            <Star size={18} weight={candidate.is_favorite ? 'fill' : 'regular'} />
                          </button>
                          <Link
                            to={`/candidates/${candidate.id}`}
                            className="text-base font-medium text-slate-900 hover:text-[#002FA7]"
                          >
                            {candidate.name || 'Unknown'}
                          </Link>
                        </div>
                        {(candidate.tags || []).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {candidate.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                              data-testid={`candidate-list-tag-${tag.id}`}
                            >
                              {tag.name}
                            </span>
                            ))}
                          </div>
                        )}
                        <span className="text-xs text-slate-500 block mt-1">Uploaded By: {candidate.uploaded_by_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {candidate.email && (
                            <div className="flex items-center space-x-2 text-sm text-slate-600">
                              <Envelope size={16} />
                              <span>{candidate.email}</span>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center space-x-2 text-sm text-slate-600">
                              <Phone size={16} />
                              <span>{candidate.phone}</span>
                            </div>
                          )}
                          {candidate.location && (
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                              <MapPin size={14} />
                              <span>{candidate.location}</span>
                            </div>
                          )}
                          {candidate.years_experience !== null && candidate.years_experience !== undefined && (
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                              <Clock size={14} />
                              <span>{candidate.years_experience} yr{candidate.years_experience === 1 ? '' : 's'} exp.</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {candidate.role && (
                          <div className="flex items-center space-x-2 text-sm text-slate-700">
                            <Briefcase size={16} />
                            <span>{candidate.role}</span>
                          </div>
                        )}
                        {candidate.role_applied_for && (
                          <div className="mt-1 text-xs text-slate-500">
                            <span className="font-medium">Applied for:</span> {candidate.role_applied_for}
                          </div>
                        )}
                        {candidate.skills && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {candidate.skills
                              .split(',')
                              .slice(0, 3)
                              .map((skill, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded"
                                >
                                  {skill.trim()}
                                </span>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {candidate.interview_rounds_count ? (
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-blue-50 text-[#002FA7] text-sm font-semibold" data-testid={`rounds-count-${candidate.id}`}>
                            {candidate.interview_rounds_count}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400" data-testid={`rounds-count-${candidate.id}`}>No Rounds</span>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        {isAiSearchActive && match_reason ? (
                          <div className="space-y-1.5" data-testid={`match-reason-${candidate.id}`}>
                            <div className="flex items-center space-x-2">
                              <Sparkle size={14} weight="fill" className="text-[#002FA7]" />
                              <span className="text-xs font-semibold text-[#002FA7]">
                                {Math.round((relevance_score || 0) * 100)}% match
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed">{match_reason}</p>
                          </div>
                        ) : candidate.latest_interview_result ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${
                              candidate.latest_interview_result === 'Pass'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                            data-testid={`interview-status-${candidate.id}`}
                          >
                            {candidate.latest_interview_result}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400" data-testid={`interview-status-${candidate.id}`}>-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <a
                            href={`${API}/candidates/${candidate.id}/cv?token=${encodeURIComponent(token || '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-slate-600 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                            title="Open CV"
                            data-testid={`open-cv-${candidate.id}`}
                          >
                            <FilePdf size={20} />
                          </a>
                          <Link
                            to={`/candidates/${candidate.id}`}
                            className="p-2 text-slate-600 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                            data-testid={`view-candidate-${candidate.id}`}
                          >
                            <Eye size={20} />
                          </Link>
                          {canDelete && (
                            <button
                              onClick={(e) => deleteCandidate(candidate.id, e)}
                              className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              data-testid={`delete-candidate-${candidate.id}`}
                            >
                              <Trash size={20} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200" data-testid="pagination-bar">
                <p className="text-sm text-slate-500">
                  Showing <strong className="text-slate-800">{(safePage - 1) * PAGE_SIZE + 1}</strong>
                  {'–'}
                  <strong className="text-slate-800">{Math.min(safePage * PAGE_SIZE, displayItems.length)}</strong> of{' '}
                  <strong className="text-slate-800">{displayItems.length}</strong> candidates
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="flex items-center space-x-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="pagination-prev"
                  >
                    <CaretLeft size={14} />
                    <span>Prev</span>
                  </button>
                  <span className="text-sm text-slate-600" data-testid="pagination-page-info">
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="flex items-center space-x-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="pagination-next"
                  >
                    <span>Next</span>
                    <CaretRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidatesList;
